import { Alert, Button } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";

import { useReviewCountsWatch } from "@/hooks/useReviewCountsWatch";
import { invalidateReviewDatasets } from "./medicalReview";

export default function ReviewHeaderStrip({
  trialId,
  onOpenSettings,
}: {
  trialId: string;
  onOpenSettings: () => void;
}) {
  const queryClient = useQueryClient();
  const { counts, hasNewData, acknowledgeNewData } =
    useReviewCountsWatch(trialId);

  const showRefreshedData = () => {
    invalidateReviewDatasets(queryClient);
    acknowledgeNewData();
  };

  const totals = counts?.counts;
  const watermark = counts?.last_scan?.watermark_at;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <StatCard
            value={totals?.sae_outstanding ?? 0}
            label="Serious events awaiting review"
            emphasis
          />
          <StatCard value={totals?.unreviewed ?? 0} label="To review" />
          <StatCard
            value={totals?.re_review ?? 0}
            label="Changed since reviewed"
          />
          {counts?.assigned_to_me !== undefined && (
            <StatCard value={counts.assigned_to_me} label="Assigned to me" />
          )}
        </div>
        <Button
          icon={<SettingOutlined />}
          onClick={onOpenSettings}
          data-testid="medical-review-settings"
        >
          Review settings
        </Button>
      </div>

      {hasNewData && (
        <Alert
          type="info"
          showIcon
          closable
          onClose={acknowledgeNewData}
          message="New data available"
          action={
            <Button size="small" type="primary" onClick={showRefreshedData}>
              Refresh
            </Button>
          }
        />
      )}

      {watermark && (
        <p className="m-0 text-xs text-[#556C75]">
          Showing data received through {new Date(watermark).toLocaleString()}.
        </p>
      )}
    </div>
  );
}

function StatCard({
  value,
  label,
  emphasis = false,
}: {
  value: number;
  label: string;
  emphasis?: boolean;
}) {
  return (
    <div className="min-w-[150px] rounded-lg border border-[#E6ECF1] bg-white px-4 py-2">
      <div
        className={`text-2xl font-semibold tabular-nums ${
          emphasis && value > 0 ? "text-[#A31D5C]" : "text-[#2B4752]"
        }`}
      >
        {value}
      </div>
      <div className="text-xs text-[#556C75]">{label}</div>
    </div>
  );
}
